import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import { setValidationAcknowledgements, useReviewCreationData } from 'reducers/reviewCreationSlice';
import { setShowViewsPanel } from 'reducers/viewsSlice';

import { NeedAttention, Settings } from 'assets/assetsV1/iconComponents';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TagOutlinedList } from 'uiToolkitV1/TagOutlinedList';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmployeeCohortList, useGetOverlappingEmployees } from '../../Hooks/dataHooks/validationDataHooks';
import { SkeletonLoaderMultiViewVariant } from '../../components/SkeletonLoaderReviewCreation';

const EmployeeRenderer: React.FC<ICellRendererParams<EmployeeCohortList>> = ({ data }) => (
  <ListItemComp
    title={data.employeeDetails.displayName}
    subTitle={data.employeeDetails.email}
    LeftComponent={() => <Avatar src={data.employeeDetails.profilePhoto} name={data.employeeDetails.displayName} />}
    RightComponent={null}
    subSubtitle={null}
    onClick={() => {}}
    isDisabled={false}
    active={false}
    withoutBackground
  />
);

const useResizeObserver = (callback: (entries: ResizeObserverEntry[]) => void) => {
  const resizeObserver = useMemo(() => new ResizeObserver(callback), [callback]);

  return resizeObserver;
};

const ViewsRenderer: React.FC<ICellRendererParams<EmployeeCohortList> & { resizeObserver: ResizeObserver }> = ({
  data,
  resizeObserver,
}) => {
  const ref = useRef();

  useEffect(() => {
    const ele = ref.current;
    resizeObserver.observe(ele);

    return () => {
      resizeObserver.unobserve(ele);
    };
  }, [resizeObserver]);

  return (
    <Box ref={ref} py={8}>
      <TagOutlinedList
        tagList={data.cohorts.map((c) => ({ id: c.id, title: c.name }))}
        onRemove={null}
        onClickHandler={null}
      />
    </Box>
  );
};

const PAGE_SIZES = [5, 10, 20];
const ROW_HEIGHT = 68;
const ROW_HEADER_HEIGHT = 40;

export const MultipleViewsValidation = () => {
  const { reviewCycleId } = useReviewCreationData();
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageNumber, setPageNumber] = useState(0);
  const gridRef = useRef<AgGridReact>();
  const { data: rowData, isLoading, isSuccess } = useGetOverlappingEmployees({ reviewCycleId });
  const dispatch = useDispatch();
  const [rowHeight, setRowHeight] = useState(68);
  const resizeObserver = useResizeObserver((entries) => {
    // + 16 for padding of 8px vertically
    const maxHeight = Math.max(...entries.map((e) => e.contentRect.height)) + 16;
    setRowHeight(Math.max(ROW_HEIGHT, maxHeight));
  });

  useEffect(() => {
    gridRef.current?.api?.resetRowHeights();
  }, [rowHeight]);

  useEffect(() => {
    if (rowData.length > 0) {
      dispatch(setValidationAcknowledgements({ key: 'hasEmployeesWithMultipleViews', value: true }));
    }
  }, [dispatch, rowData.length]);

  const gridStyle = useMemo(() => {
    const noOfRowsOnLastPage = rowData.length - pageSize * pageNumber;
    return {
      height: `calc(${rowHeight + 1}px * ${Math.min(pageSize, noOfRowsOnLastPage)} + ${ROW_HEADER_HEIGHT}px)`,
      width: '100%',
    };
  }, [pageNumber, pageSize, rowData.length, rowHeight]);

  const columnDefs = useMemo<ColDef<EmployeeCohortList>[]>(
    () => [
      {
        headerName: 'Employee',
        cellRenderer: EmployeeRenderer,
        valueGetter: ({ data }) => data.employeeDetails.displayName,
      },
      {
        headerName: 'Views',
        cellRenderer: ViewsRenderer,
        cellRendererParams: {
          resizeObserver,
        },
        valueGetter: ({ data }) => data.cohorts.map((c) => c.name).join(' '),
      },
      {
        headerName: 'Department',
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.employeeDetails.employeeFunction,
      },
      {
        headerName: 'Designation',
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.employeeDetails.designation,
      },
    ],
    [resizeObserver]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      headerComponent: AgGridHeader,
      headerComponentParams: {
        enableSorting: true,
        enableMenu: true,
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  const onPageChange = (_, page: number) => {
    setPageNumber(page);
    gridRef.current.api.paginationGoToPage(page);
  };
  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
  };

  const openManageViewsPanel = () => {
    dispatch(
      // todo: vikalp review manage changes refetch
      setShowViewsPanel({})
    );
  };

  if (isLoading) {
    return <SkeletonLoaderMultiViewVariant />;
  }

  // don't show this section if no pending validations
  if (rowData.length === 0) {
    return null;
  }

  const gridStatePersistenceKey = null;

  return (
    <ExpandablePaper>
      <ExpandablePaperHeader
        title="Employees across multiple views"
        subTitle="These employees are present in multiple views, unless you edit the view you cannot publish this review cycle"
        leftIconComponent={<NeedAttention boxSize="24px" color="icon.g2" />}
        rightComponent={
          <TextBodyMedium bold color="functional.C62C02" mr={3}>
            {rowData.length} Employees
          </TextBodyMedium>
        }
      />
      <ExpandablePaperBody p="8px 8px 0">
        <div style={gridStyle} className="ag-theme-alpine">
          <AgGridWrapper
            gridStatePersistenceKey={gridStatePersistenceKey}
            useFilterModelPersistence={false}
            useColumnStatePersistence={false}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            paginationPageSize={pageSize}
            pagination
            suppressPaginationPanel
            suppressScrollOnNewData
            rowHeight={rowHeight}
            headerHeight={ROW_HEADER_HEIGHT}
            rowStyle={{ cursor: 'default' }}
          />
        </div>
        <Flex alignItems="center" justifyContent="space-between" m="8px 8px 0">
          <Button
            onClick={openManageViewsPanel}
            variant="tertiary"
            StartIcon={() => <Settings boxSize="24px" />}
            withBackground={false}
          >
            Manage Views
          </Button>
          <TablePagination
            count={rowData.length}
            onPageChange={onPageChange}
            rowsPerPage={pageSize}
            page={pageNumber}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZES}
          />
        </Flex>
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
